/**
 * Lets svgcanvas code make DOM changes that are not document changes (for example dimming elements while editing
 * inside a group, or temporarily re-attaching metadata to serialize it) without them being reported to Blazor, which
 * owns the document. The document sync service registers the real implementation; until then this is a no-op wrapper,
 * so svgcanvas has no dependency on it.
 */

type UntrackedRunner = <T>(fn: () => T) => T;
type TransformListAccessHandler = (elem: Element) => void;

let untrackedRunner: UntrackedRunner = fn => fn();
let transformListAccessHandler: TransformListAccessHandler = () => { /* Nothing tracking yet. */ };

/**
 * Registers the function told whenever an element's transform list is handed out for possible editing.
 * @param handler Called with the element the list belongs to.
 */
export function setTransformListAccessHandler(handler: TransformListAccessHandler) {
    transformListAccessHandler = handler;
}

/**
 * Notes that an element's transform list is about to be read or edited.
 *
 * Edits made through `SVGTransformList` (rather than `setAttribute`) only reach the `transform` attribute lazily in
 * Chromium, and produce no mutation record, so they are invisible to a MutationObserver. The tracker uses this to
 * compare the attribute before and after instead.
 * @param elem The element the transform list belongs to.
 */
export function noteTransformListAccess(elem: Element) {
    transformListAccessHandler(elem);
}

/**
 * Registers the function that runs code with document change tracking suspended.
 * @param runner Runs the given function untracked and returns its result.
 */
export function setUntrackedRunner(runner: UntrackedRunner) {
    untrackedRunner = runner;
}

/**
 * Runs `fn` without reporting the DOM changes it makes as document changes. Changes made before this call are still
 * reported.
 * @param fn The function to run.
 * @returns Whatever `fn` returns.
 */
export function runUntracked<T>(fn: () => T): T {
    return untrackedRunner(fn);
}
